import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MaterialReactTable } from 'material-react-table';
import {
  Box,
  Button,
  createTheme,
  ThemeProvider,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import AddColumnToDepartmentPropertyButton from './AddColumnToDepartmentPropertyButton';
import Dropdown from '../../../inputs/Dropdown';
import TabEditVehicleColumn from './TabEditVehicleColumn';
import ConfirmationDeleteModal from '../../../components/ConfirmationDeleteModal';
import {
  deleteDepartmentProperty,
  updateDepartmentProperty,
  updateDepartmentPropertiesOrder,
  updateVehicleColumn,
  addNewVehicleColumn,
} from '../../../actions/apiDepartmentUpdates';

const TabEditDepartmentProperties = () => {
  const dispatch = useDispatch();
  const globalTheme = useTheme();

  const [modalOpen, setModalOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState(null);
  const [tableKey, setTableKey] = useState(0);
  const [error, setError] = useState(null);
  const [localVehicleColumns, setLocalVehicleColumns] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [inputName, setInputName] = useState('');
  const [addColumnError, setAddColumnError] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  const vehicle_columns = useSelector((state) => state.settings_departments.vehicle_columns);
  const department_properties = useSelector((state) => state.settings_admin.edit_department_properties);
  const set_dealerships = useSelector((state) => state.settings_dealerships.related_installations);
  const edit_department = useSelector((state) => state.settings_admin.edit_department);
  const original_departments = useSelector((state) => state.settings_departments.original_departments);
  const input_types = useSelector((state) => state.settings_departments.input_types);

  useEffect(() => {
    setLocalVehicleColumns(vehicle_columns || []);
  }, [vehicle_columns]);

  // Only increment tableKey for structural changes (add/delete columns)
  useEffect(() => {
    setTableKey((prev) => prev + 1);
  }, [vehicle_columns?.length]);

  const iconList = useMemo(
    () => [
      'far fa-paperclip',
      'far fa-clock',
      'far fa-alarm-clock',
      'far fa-cogs',
      'far fa-usd-square',
      'far fa-dollar-sign',
      'far fa-camera-retro',
      'far fa-camera-alt',
      'far fa-clipboard-list',
      'far fa-car',
      'far fa-clipboard-check',
      'far fa-tasks',
      'far fa-chart-bar',
      'far fa-ellipsis-h-alt',
      'far fa-receipt',
      'far fa-calendar-plus',
      'far fa-check-circle',
    ],
    [],
  );

  const inputTypesSorted = useMemo(() => {
    const newObj = Object.keys(input_types).map((key) => input_types[key]);
    return newObj.sort((a, b) => a.name.localeCompare(b.name));
  }, [input_types]);

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'light',
          primary: {
            main: '#007bff',
          },
        },
        components: {
          MuiCheckbox: {
            styleOverrides: {
              root: {
                '&.Mui-checked': {
                  color: '#007bff',
                },
                '&:hover': {
                  backgroundColor: 'rgba(0, 123, 255, 0.04)',
                },
              },
            },
          },
        },
      }),
    [globalTheme],
  );

  const departmentPropertiesColumns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Property Name',
        size: 150,
        enableEditing: false,
      },
      {
        accessorKey: 'delete',
        header: '',
        size: 80,
        enableEditing: false,
        Cell: ({ row }) => (
          <Button
            variant="contained"
            color="error"
            size="small"
            className="btn btn-danger btn-sm"
            onClick={() => {
              setDeleteType('department_property');
              setDeleteId(row.original.id[0]);
              setDeleteName(row.original.name[0] || 'this item');
              setModalOpen(true);
            }}
          >
            Delete
          </Button>
        ),
      },
    ],
    [set_dealerships, dispatch],
  );

  const vehicleColumns = useMemo(
    () => [
      {
        accessorKey: 'addto',
        header: 'Add +',
        size: 120,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <AddColumnToDepartmentPropertyButton vehicle_column={row} />
          </Box>
        ),
      },
      {
        accessorKey: 'icon',
        header: 'Icon',
        enableEditing: true,
        editVariant: 'select',
        muiEditTextFieldProps: ({ row, table }) => ({
          select: true,
          value: row.original.icon[0] || '',
          onChange: async (event) => {
            const iconValue = event.target.value;
            const id = row.original.vehicle_column_id[0];
            setLocalVehicleColumns((prev) =>
              prev.map((col) =>
                col.id === id ? { ...col, icon: iconValue } : col,
              ),
            );
            try {
              await dispatch(updateVehicleColumn(id, { icon: iconValue }, 'editVehicleColumnError'));
              setError(null);
              table.setEditingCell(null);
            } catch (ex) {
              setLocalVehicleColumns(vehicle_columns || []);
              setError('Failed to update icon. Please try again.');
              console.error('Icon update error:', ex);
            }
          },
          onClick: (event) => event.stopPropagation(),
          SelectProps: {
            renderValue: (selected) => (selected ? <i className={selected} /> : 'None'),
            MenuProps: {
              PaperProps: {
                style: { maxHeight: 300 },
              },
            },
            sx: { zIndex: 1000 },
          },
          children: [
            <MenuItem key="none" value="">
              None
            </MenuItem>,
            ...iconList.map((icon) => (
              <MenuItem key={icon} value={icon}>
                <i className={icon} style={{ marginRight: '8px' }} />
                {icon}
              </MenuItem>
            )),
          ],
        }),
        Cell: ({ row }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: row.original.icon_bg_color[0] || 'transparent',
              color: row.original.icon_font_color[0] || 'inherit',
              padding: '8px',
            }}
          >
            {row.original.icon[0] ? <i className={row.original.icon[0]} /> : 'None'}
          </Box>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        size: 120,
      },
      {
        accessorKey: 'input_name',
        header: 'Input Type',
        size: 120,
      },
      {
        accessorKey: 'default_value',
        header: 'Default Value',
        size: 120,
      },
      {
        accessorKey: 'has_default_value',
        header: 'Has Default Value',
        size: 120,
        Cell: ({ cell }) => cell.getValue()?.[0]?.toString() || '',
      },
      {
        accessorKey: 'automove',
        header: 'Automove',
        size: 120,
        Cell: ({ cell }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span>{cell.getValue()?.[0] ? 'Yes' : ''}</span>
          </Box>
        ),
      },
    ],
    [dispatch, iconList],
  );

  const departmentPropertiesData = useMemo(
    () =>
      department_properties
        ?.slice()
        ?.sort((a, b) => (a.order || 0) - (b.order || 0))
        ?.map((property) => ({
          id: [property.id],
          department_id: [property.department_id],
          vehicle_column_id: [property.vehicle_column_id],
          order: [property.order || 0],
          name: [property.vehicle_column.name],
          dealership: [property.dealership || ''],
        })) || [],
    [department_properties],
  );

  const vehicleColumnsData = useMemo(
    () =>
      localVehicleColumns.map((vehicle_column) => ({
        name: [vehicle_column.name],
        input_name: [vehicle_column.input_name],
        automove: [vehicle_column.automove],
        org: [vehicle_column],
        default_value: [vehicle_column.default_value],
        has_default_value: [vehicle_column.has_default_value],
        vehicle_column_id: [vehicle_column.id],
        icon: [vehicle_column.icon || ''],
        icon_bg_color: [vehicle_column.icon_bg_color || ''],
        icon_font_color: [vehicle_column.icon_font_color || ''],
      })),
    [localVehicleColumns],
  );

  const handleOpenAddModal = () => {
    setAddModalOpen(true);
    setNewColumnName('');
    setInputName('');
    setAddColumnError(false);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setNewColumnName('');
    setInputName('');
    setAddColumnError(false);
  };

  const handleAddVehicleColumn = () => {
    if (!newColumnName.trim() || !inputName) {
      setAddColumnError(true);
      return;
    }

    const data = {
      name: newColumnName,
      input_name: inputName,
    };

    dispatch(addNewVehicleColumn(data, 'addVehicleColumnError')).then((resp) => {
      if (resp === 'success') {
        setAddColumnError(false);
        handleCloseAddModal();
      } else {
        const errorMessage = resp?.error?.invalid_params?.name?.[0] || 'An error occurred while adding the column';
        setAddColumnError(true);
      }
    });
  };

  const tableRef = useRef();
  const departmentPropertiesTableRef = useRef();

  const handleConfirmDelete = () => {
    if (deleteType === 'department_property') {
      dispatch(deleteDepartmentProperty(edit_department.id, deleteId, 'deleteDepartmentPropertyError'));
    }
    setModalOpen(false);
    setDeleteType(null);
    setDeleteId(null);
    setDeleteName(null);
    setTableKey((prev) => prev + 1);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setDeleteType(null);
    setDeleteId(null);
    setDeleteName(null);
  };

  const handleRowOrderChange = (draggingRow, hoveredRow) => {
    const newData = [...departmentPropertiesData];
    if (hoveredRow && draggingRow) {
      newData.splice(hoveredRow.index, 0, newData.splice(draggingRow.index, 1)[0]);
      const newOrder = newData.map((item) => item.id[0]);
      dispatch(updateDepartmentPropertiesOrder(edit_department.id, newOrder, 'updateDepartmentPropertiesOrderError'));
      setTableKey((prev) => prev + 1);
    }
  };

  return (
    <>
      <ConfirmationDeleteModal
        isOpen={modalOpen}
        onRequestClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        message={`Are you sure you want to delete ${deleteName || 'this item'} a department property?`}
      />
      <Dialog open={addModalOpen} onClose={handleCloseAddModal}>
        <DialogTitle>Add Vehicle Column</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Column Name"
            type="text"
            fullWidth
            value={newColumnName}
            onChange={(e) => setNewColumnName(e.target.value)}
            error={addColumnError && !newColumnName.trim()}
            helperText={addColumnError && !newColumnName.trim() ? 'Please enter a valid column name' : ''}
          />
          <FormControl fullWidth margin="dense" error={addColumnError && !inputName}>
            <InputLabel>Input Type</InputLabel>
            <Select
              value={inputName}
              onChange={(e) => setInputName(e.target.value)}
              label="Input Type"
            >
              <MenuItem value="">Select Input Type</MenuItem>
              {inputTypesSorted.map((option) => (
                <MenuItem key={option.name} value={option.name}>
                  {`${option.name} (${option.description})`}
                </MenuItem>
              ))}
            </Select>
            {addColumnError && !inputName && (
              <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>
                Please select an input type
              </Box>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddModal} color="error">
            Cancel
          </Button>
          <Button onClick={handleAddVehicleColumn} color="success">
            Add
          </Button>
        </DialogActions>
      </Dialog>
      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="fa fa-exclamation-circle me-2" />
          {error}
        </div>
      )}
      <div className="container-fluid">
        <div className="col-md-12">
          <h5 className="text-success text-center text-capitalize">
            {edit_department?.name || 'No Department Selected'}
            <br />
            <small className="text-warning text-center">Contact support if you need any assistance.</small>
          </h5>
          <div className="row">
            <div className="card col-md-5">
              <div className={edit_department?.name ? 'card-header bg-success text-white' : 'card-header bg-warning text-white'}>
                <div className="p-2 text-capitalize">
                  {edit_department?.name || 'Select Departments'}
                </div>
              </div>
              <div className="card-body">
                <div className="d-flex flex-column">
                  <div className="p-2">
                    <div className="col-12">
                      <small>
                        <i className="fa fa-info-circle text-info" />
                        {' '}
                        Drag and Drop rows to change order
                      </small>
                    </div>
                    <div className="p-2 list-group setscroll">
                      <ThemeProvider theme={tableTheme}>
                        <MaterialReactTable
                          tableRef={departmentPropertiesTableRef}
                          columns={departmentPropertiesColumns}
                          data={departmentPropertiesData}
                          editDisplayMode="cell"
                          enableEditing
                          enableRowOrdering
                          enableRowDragging
                          enableSorting={false}
                          enableColumnFilters
                          initialState={{
                            showColumnFilters: true,
                          }}
                          muiRowDragHandleProps={({ table }) => ({
                            onDragEnd: () => {
                              const { draggingRow, hoveredRow } = table.getState();
                              if (hoveredRow && draggingRow) {
                                handleRowOrderChange(draggingRow, hoveredRow);
                              }
                            },
                          })}
                          sx={{ minHeight: '300px' }}
                        />
                      </ThemeProvider>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-footer" />
            </div>

            <div className="card col-md-7">
              <div className="card-header bg-dark text-white">
                <div className="row">
                  <div className="col-6">
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleOpenAddModal}
                    >
                      Create New Column
                    </Button>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="d-flex flex-column">
                  <div className="p-2 list-group setscroll">
                    <ThemeProvider theme={tableTheme}>
                      <MaterialReactTable
                        key={tableKey}
                        tableRef={tableRef}
                        columns={vehicleColumns}
                        data={vehicleColumnsData}
                        editDisplayMode="cell"
                        enableEditing
                        enableSorting={false}
                        enablePagination={false}
                        enableColumnFilters
                        initialState={{ showColumnFilters: true }}
                        state={{ expanded: expandedRows }}
                        onExpandedChange={(updater) => {
                          const newExpanded = typeof updater === 'function' ? updater(expandedRows) : updater;
                          setExpandedRows(newExpanded);
                        }}
                        renderDetailPanel={({ row }) => (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-around',
                              alignItems: 'center',
                            }}
                          >
                            <TabEditVehicleColumn vehicle_column_id={row.original.vehicle_column_id[0]} />
                          </Box>
                        )}
                      />
                    </ThemeProvider>
                  </div>
                </div>
              </div>
              <div className="card-footer" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TabEditDepartmentProperties;